import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCustomers } from "@/contexts/CustomerContext";
import { useProducts } from "@/contexts/ProductContext";
import { Users, CheckCircle, MessageCircle, Send, Sparkles, Package, ArrowRight, TrendingUp, Lightbulb, Clock, Zap, Target, BarChart3, Phone, AlertTriangle } from "lucide-react";
import "./Dashboard.css";

function timeAgo(date: string) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

const SALES_TIPS = [
  "Ask open-ended questions to uncover your customer's true needs — don't just pitch features.",
  "Follow up within 24 hours. Speed wins deals in furniture sales.",
  "Mirror your customer's language. If they say 'cozy', use 'cozy' — not 'comfortable'.",
  "Always walk the customer to the door. The last impression is as important as the first.",
  "Suggest complementary pieces — a coffee table with a sofa, nightstands with a bed frame.",
  "Use the 'feel, felt, found' method: 'I understand how you feel, others have felt the same, and they found...'",
  "Take a photo of the customer's room if they allow it — personalized recommendations close deals.",
];

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  hot_lead: { label: "Hot Lead", color: "#F97316", bg: "#FFF7ED" },
  active: { label: "Active", color: "#10B981", bg: "#ECFDF5" },
  new: { label: "New", color: "#1B6EF3", bg: "#E8F0FE" },
  stalled: { label: "Stalled", color: "#EAB308", bg: "#FEFCE8" },
  closed: { label: "Closed", color: "#6B7280", bg: "#F3F4F6" },
};

function getDailyTip() {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  return SALES_TIPS[dayOfYear % SALES_TIPS.length];
}

export default function Dashboard() {
  const { user, trialStatus } = useAuth();
  const { customers } = useCustomers();
  const { products } = useProducts();
  const navigate = useNavigate();

  const activeChats = customers.filter((c) => c.status === "active" || c.status === "hot_lead").length;
  const closed = customers.filter((c) => c.status === "closed").length;
  const newThisWeek = customers.filter((c) => {
    const created = new Date(c.createdAt).getTime();
    return Date.now() - created < 7 * 86400000;
  }).length;
  const recentCustomers = [...customers].sort((a, b) => new Date(b.lastContact).getTime() - new Date(a.lastContact).getTime()).slice(0, 5);

  const stats = [
    { label: "Active Chats", value: activeChats, icon: MessageCircle, gradient: "linear-gradient(135deg, #1B6EF3, #3B82F6)", shadowColor: "rgba(27, 110, 243, 0.3)" },
    { label: "Total Customers", value: customers.length, icon: Users, gradient: "linear-gradient(135deg, #8B5CF6, #A78BFA)", shadowColor: "rgba(139, 92, 246, 0.3)" },
    { label: "Products", value: products.length, icon: Package, gradient: "linear-gradient(135deg, #F97316, #FB923C)", shadowColor: "rgba(249, 115, 22, 0.3)" },
    { label: "Closed Deals", value: closed, icon: CheckCircle, gradient: "linear-gradient(135deg, #10B981, #34D399)", shadowColor: "rgba(16, 185, 129, 0.3)" },
  ];

  const quickActions = [
    { label: "AI Follow-Up Autopilot", desc: "Generate personalized follow-ups", icon: Send, route: "/follow-ups", accentColor: "#1B6EF3", bgColor: "#E8F0FE", featured: true },
    { label: "Ask Furniture Fred", desc: "AI sales co-pilot", icon: Sparkles, route: "/fred", accentColor: "#8B5CF6", bgColor: "#F5F3FF", featured: false },
    { label: "Product Catalog", desc: "Browse and import products", icon: Package, route: "/products", accentColor: "#F97316", bgColor: "#FFF7ED", featured: false },
  ];

  const conversionRate = customers.length > 0 ? Math.round((closed / customers.length) * 100) : 0;
  const contactedToday = customers.filter((c) => {
    const last = new Date(c.lastContact).getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return last >= today.getTime();
  }).length;

  return (
    <div className="page">
      {trialStatus.isOnTrial && trialStatus.daysLeft <= 3 && (
        <div style={{
          background: "linear-gradient(135deg, #FEF3C7, #FDE68A)",
          border: "1px solid #F59E0B",
          borderRadius: 12,
          padding: "14px 20px",
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          <AlertTriangle size={20} color="#D97706" />
          <div style={{ flex: 1 }}>
            <strong style={{ color: "#92400E" }}>
              {trialStatus.daysLeft === 0 ? "Your trial expires today!" : `Your trial expires in ${trialStatus.daysLeft} day${trialStatus.daysLeft !== 1 ? "s" : ""}!`}
            </strong>
            <p style={{ margin: 0, fontSize: 13, color: "#92400E", opacity: 0.8 }}>
              Subscribe at $99/month to keep using all features.
            </p>
          </div>
          <button
            className="btn btn-primary btn-sm"
            style={{ background: "#D97706", borderColor: "#D97706", whiteSpace: "nowrap" }}
            onClick={() => navigate("/settings")}
          >
            Subscribe Now
          </button>
        </div>
      )}
      <div className="dash-welcome-banner">
        <div className="dash-orb dash-orb-1" />
        <div className="dash-orb dash-orb-2" />
        <div className="dash-orb dash-orb-3" />
        <div className="dash-welcome-content">
          <div className="dash-welcome-text">
            <span className="dash-greeting">{getGreeting()},</span>
            <h1 className="dash-name">{user?.name?.split(" ")[0]}</h1>
            <p className="dash-meta">
              <Clock size={14} />
              {user?.storeLocation} &middot; {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </p>
          </div>
          <div className="dash-welcome-right">
            <div className="dash-welcome-icon">
              <TrendingUp size={48} strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>

      <div className="dash-performance-strip">
        <div className="dash-perf-item">
          <div className="dash-perf-icon" style={{ background: "linear-gradient(135deg, #10B981, #34D399)" }}>
            <Target size={16} />
          </div>
          <div className="dash-perf-info">
            <span className="dash-perf-value">{conversionRate}%</span>
            <span className="dash-perf-label">Close Rate</span>
          </div>
        </div>
        <div className="dash-perf-divider" />
        <div className="dash-perf-item">
          <div className="dash-perf-icon" style={{ background: "linear-gradient(135deg, #1B6EF3, #3B82F6)" }}>
            <Phone size={16} />
          </div>
          <div className="dash-perf-info">
            <span className="dash-perf-value">{contactedToday}</span>
            <span className="dash-perf-label">Contacted Today</span>
          </div>
        </div>
        <div className="dash-perf-divider" />
        <div className="dash-perf-item">
          <div className="dash-perf-icon" style={{ background: "linear-gradient(135deg, #F97316, #FB923C)" }}>
            <Zap size={16} />
          </div>
          <div className="dash-perf-info">
            <span className="dash-perf-value">{newThisWeek}</span>
            <span className="dash-perf-label">New This Week</span>
          </div>
        </div>
        <div className="dash-perf-divider" />
        <div className="dash-perf-item">
          <div className="dash-perf-icon" style={{ background: "linear-gradient(135deg, #8B5CF6, #A78BFA)" }}>
            <BarChart3 size={16} />
          </div>
          <div className="dash-perf-info">
            <span className="dash-perf-value">{activeChats}</span>
            <span className="dash-perf-label">In Pipeline</span>
          </div>
        </div>
      </div>

      <div className="dash-stats-grid">
        {stats.map((s) => (
          <div className="dash-stat-card" key={s.label} style={{ background: s.gradient, boxShadow: `0 8px 24px ${s.shadowColor}` }}>
            <div className="dash-stat-card-bg" />
            <div className="dash-stat-icon">
              <s.icon size={22} />
            </div>
            <div className="dash-stat-value">{s.value}</div>
            <div className="dash-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="dash-grid">
        <div className="dash-left-col">
          <div className="card dash-quick-actions-card">
            <h3>Quick Actions</h3>
            <div className="dash-quick-actions">
              {quickActions.map((a) => (
                <button
                  className={`dash-action-btn ${a.featured ? "dash-action-featured" : ""}`}
                  key={a.label}
                  onClick={() => navigate(a.route)}
                >
                  <div className="dash-action-icon" style={{ background: a.bgColor, color: a.accentColor }}>
                    <a.icon size={20} />
                  </div>
                  <div className="dash-action-text">
                    <strong>{a.label}</strong>
                    <span>{a.desc}</span>
                  </div>
                  <div className="dash-action-arrow" style={{ color: a.accentColor }}>
                    <ArrowRight size={16} />
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="dash-tip-card">
            <div className="dash-tip-glow" />
            <div className="dash-tip-header">
              <div className="dash-tip-icon-wrap">
                <Lightbulb size={18} />
              </div>
              <h3>Daily Sales Tip</h3>
            </div>
            <p className="dash-tip-text">{getDailyTip()}</p>
          </div>
        </div>

        <div className="card dash-activity-card">
          <div className="dash-activity-header">
            <h3>Recent Activity</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate("/customers")}>See All</button>
          </div>
          {recentCustomers.length === 0 ? (
            <div className="empty-state"><p>No customers yet</p></div>
          ) : (
            <div className="dash-activity-list">
              {recentCustomers.map((c) => {
                const conf = STATUS_CONFIG[c.status] || STATUS_CONFIG.new;
                return (
                  <div key={c.id} className="dash-activity-row" onClick={() => navigate(`/customers/${c.id}`)}>
                    <div className="dash-activity-left">
                      <div className="dash-activity-avatar" style={{ background: conf.bg, color: conf.color }}>
                        {c.name.split(" ").map((n) => n[0]).join("")}
                      </div>
                      <div className="dash-activity-info">
                        <div className="dash-activity-name">{c.name}</div>
                        <div className="dash-activity-meta">
                          <span>{c.phone}</span>
                          {c.needs && <span className="dash-activity-needs">{c.needs}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="dash-activity-right">
                      <span className="dash-activity-status" style={{ background: conf.bg, color: conf.color, borderColor: conf.color }}>
                        {conf.label}
                      </span>
                      {c.budget && <span className="dash-activity-budget">{c.budget}</span>}
                      <span className="dash-activity-time">{timeAgo(c.lastContact)}</span>
                      <ArrowRight size={16} className="dash-activity-arrow" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
